"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import {
  signupSchema,
  normalizePhoneE164,
  type SignupFormValues,
} from "@/schema/signup";
import {
  signup,
  registerContractor,
  parseApiErrorMessage,
} from "@/services/auth.service";
import { useRouter } from "next-nprogress-bar";
import { signIn } from "next-auth/react";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";

type UseSignupResult = {
  form: ReturnType<typeof useForm<SignupFormValues>>;
  accountType: "homeowner" | "contractor";
  setAccountType: (t: "homeowner" | "contractor") => void;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: SignupFormValues) => Promise<any>;
  clearError: () => void;
  // UI interaction state/handlers moved from component
  showPassword: boolean;
  togglePasswordVisibility: () => void;
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void> | void;
  // Google signup flow
  showAccountTypeDialog: boolean;
  setShowAccountTypeDialog: (open: boolean) => void;
  handleGoogleSignup: () => void;
  handleAccountTypeConfirm: (t: "homeowner" | "contractor") => Promise<void>;
  googleLoading: boolean;
};

type UseSignupOptions = {};

export function useSignup(options?: UseSignupOptions): UseSignupResult {
  const router = useRouter();
  const [accountType, setAccountType] = useState<"homeowner" | "contractor">(
    "homeowner"
  );
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountTypeDialog, setShowAccountTypeDialog] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Reset Google loading when user returns (back/forward cache or visibility change)
  useEffect(() => {
    const handlePageShow = () => setGoogleLoading(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setGoogleLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      type: accountType,
      name: "",
      email: "",
      password: "",
      mobilePhone: "",
    },
  });

  // Keep the form's `type` field in sync with the tab selection so the
  // schema's contractor-only mobilePhone rule validates against the tab
  // the user actually has selected.
  const selectAccountType = useCallback(
    (t: "homeowner" | "contractor") => {
      setAccountType(t);
      form.setValue("type", t);
      form.clearErrors("mobilePhone");
    },
    [form]
  );

  const signupMutation = useMutation({
    mutationFn: async (values: SignupFormValues) => {
      if (accountType === "contractor") {
        // Contractors go through the SMS-OTP endpoint; the sessionId in the
        // response drives the verify-phone step.
        return await registerContractor({
          fullName: values.name,
          email: values.email,
          mobilePhone: normalizePhoneE164(values.mobilePhone ?? "") ?? "",
          password: values.password,
        });
      }
      const payload: SignupFormValues = { ...values, type: accountType };
      return await signup(payload);
    },
    onError: (err: any) => {
      const errorMessage = parseApiErrorMessage(err);
      setError(errorMessage);
    },
    onSuccess: (data: any) => {
      setError(null);
      if (accountType === "contractor") {
        const sessionId = data?.sessionId ?? form.getValues("email");
        router.push(`/signup/verify-phone/${encodeURIComponent(sessionId)}`);
        return;
      }
      router.push(`/signup/confirm/${form.getValues("email")}`);
    },
  });

  const submitting = signupMutation.isPending;

  const onSubmit = useCallback(
    async (values: SignupFormValues) => {
      try {
        setError(null);
        const result = await signupMutation.mutateAsync(values);
        return { success: true, data: result, email: values.email };
      } catch (err: any) {
        return {
          success: false,
          error: error || "Signup failed. Please try again.",
        };
      }
    },
    [signupMutation, error]
  );

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleGoogleSignup = useCallback(() => {
    setShowAccountTypeDialog(true);
  }, []);

  const handleAccountTypeConfirm = useCallback(
    async (type: "homeowner" | "contractor") => {
      setShowAccountTypeDialog(false);
      setGoogleLoading(true);
      try {
        sessionStorage.setItem("signupAccountType", type);
        await signIn("google", { callbackUrl: "/callback" });
      } catch (err: any) {
        setGoogleLoading(false);
        showToast(
          TOAST_TYPES.error,
          "Google sign-in failed. Please try again."
        );
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return useMemo(
    () => ({
      form,
      accountType,
      setAccountType: selectAccountType,
      submitting,
      error,
      onSubmit,
      clearError,
      showPassword,
      togglePasswordVisibility,
      handleSubmit,
      showAccountTypeDialog,
      setShowAccountTypeDialog,
      handleGoogleSignup,
      handleAccountTypeConfirm,
      googleLoading,
    }),
    [
      form,
      accountType,
      selectAccountType,
      submitting,
      error,
      onSubmit,
      clearError,
      showPassword,
      handleSubmit,
      showAccountTypeDialog,
      googleLoading,
      handleAccountTypeConfirm,
      handleGoogleSignup,
      togglePasswordVisibility,
    ]
  );
}

export default useSignup;
